import React, { useState, useMemo, useCallback } from "react";
import { useQuery } from "@tanstack/react-query";
import api, { formatVND } from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Download, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight,
  Plus, List, LayoutGrid, ChevronUp, ChevronDown, Save, Edit2,
} from "lucide-react";
import { MobileFilterToggle, MobileFilterClose, sidebarClasses } from "@/components/PageWithSidebar";

export default function PriceBookPage() {
  const [showFilter, setShowFilter] = useState(false);
  const [selectedBook, setSelectedBook] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedGroup, setSelectedGroup] = useState("Tất cả");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [priceChanges, setPriceChanges] = useState({});
  const [saving, setSaving] = useState(false);
  const [createBookDialog, setCreateBookDialog] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState({});

  // Fetch pricebooks
  const { data: booksData } = useQuery({
    queryKey: ["pricebooks"],
    queryFn: () => api.get("/pricebook/books").then(r => r.data.pricebooks || []),
  });
  const books = booksData || [];

  // Fetch product groups
  const { data: groupsData } = useQuery({
    queryKey: ["product-groups"],
    queryFn: () => api.get("/pricebook/groups").then(r => r.data.groups || []),
  });
  const groups = groupsData || [];

  // Fetch products with prices
  const queryParams = useMemo(() => ({
    pricebook_id: selectedBook || undefined,
    search: searchQuery || undefined,
    group: selectedGroup !== "Tất cả" ? selectedGroup : undefined,
    page, page_size: pageSize,
  }), [selectedBook, searchQuery, selectedGroup, page, pageSize]);

  const { data: productsData, isLoading, refetch } = useQuery({
    queryKey: ["pricebook-products", queryParams],
    queryFn: () => api.get("/pricebook/products", { params: queryParams }).then(r => r.data),
    keepPreviousData: true,
  });

  const products = productsData?.products || [];
  const totalCount = productsData?.total || 0;
  const totalPages = Math.ceil(totalCount / pageSize);

  const currentBookName = books.find(b => b.id === selectedBook)?.name || books[0]?.name || "Bảng giá chung";

  // Handle price change
  const handlePriceChange = (productId, value) => {
    setPriceChanges(prev => ({ ...prev, [productId]: Number(value) || 0 }));
  };

  // Save all price changes
  const handleSaveAll = async () => {
    const updates = Object.entries(priceChanges).map(([product_id, new_price]) => ({ product_id, new_price }));
    if (updates.length === 0) { toast.info("Chưa có thay đổi giá nào"); return; }
    setSaving(true);
    try {
      const res = await api.post("/pricebook/update-prices", { pricebook_id: selectedBook || null, updates });
      toast.success(res.data.message);
      setPriceChanges({});
      refetch();
    } catch (err) { toast.error("Lỗi: " + (err.response?.data?.detail || err.message)); }
    finally { setSaving(false); }
  };

  // Export
  const handleExport = () => {
    const headers = ["Mã hàng hóa", "Tên hàng", "Giá vốn", "Đơn giá nhập cuối", "Giá bán"];
    const rows = products.map(p => [p.code, p.name, p.cost_price, p.last_import_price, p.sell_price]);
    const csv = [headers.join(","), ...rows.map(r => r.join(","))].join("\n");
    const blob = new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = `bang_gia_${new Date().toISOString().slice(0,10)}.csv`; a.click();
    URL.revokeObjectURL(url); toast.success("Đã xuất file CSV");
  };

  const hasChanges = Object.keys(priceChanges).length > 0;

  return (
    <div className="flex h-full bg-white dark:bg-slate-900" data-testid="pricebook-page">
      {/* Left Sidebar */}
      <aside className={sidebarClasses(showFilter)}>
        <MobileFilterClose setShowFilter={setShowFilter} />
        {/* Bảng giá */}
        <div>
          <h4 className="text-sm font-semibold text-gray-700 dark:text-slate-300 mb-2 flex items-center justify-between">
            Bảng giá
            <button onClick={() => setCreateBookDialog(true)} className="text-gray-400 hover:text-blue-600"><Plus className="w-4 h-4" /></button>
          </h4>
          <div className="flex items-center gap-1">
            <select
              value={selectedBook}
              onChange={e => { setSelectedBook(e.target.value); setPage(1); }}
              className="flex-1 text-sm border border-gray-300 dark:border-slate-600 rounded px-2 py-1.5 bg-white dark:bg-slate-700 dark:text-slate-200"
              data-testid="pricebook-select"
            >
              <option value="">Bảng giá chung</option>
              {books.filter(b => !b.is_default).map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
            </select>
            <button className="p-1 text-gray-400 hover:text-blue-600"><Edit2 className="w-4 h-4" /></button>
          </div>
        </div>

        {/* Tìm kiếm */}
        <div>
          <h4 className="text-sm font-semibold text-gray-700 dark:text-slate-300 mb-2 flex items-center justify-between">Tìm kiếm<ChevronUp className="w-4 h-4" /></h4>
          <Input placeholder="Theo mã, tên hàng (F3)" value={searchQuery} onChange={e=>{setSearchQuery(e.target.value);setPage(1);}} className="text-sm" data-testid="pricebook-search" />
        </div>

        {/* Nhóm hàng */}
        <div>
          <h4 className="text-sm font-semibold text-gray-700 dark:text-slate-300 mb-2 flex items-center justify-between">Nhóm hàng<ChevronUp className="w-4 h-4" /></h4>
          <Input placeholder="Tìm kiếm nhóm hàng" className="text-sm mb-2" />
          <div className="space-y-0.5 max-h-[300px] overflow-y-auto">
            <button
              onClick={() => { setSelectedGroup("Tất cả"); setPage(1); }}
              className={`w-full text-left px-2 py-1 text-sm rounded ${selectedGroup === "Tất cả" ? "bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 font-medium" : "text-gray-600 dark:text-slate-400 hover:bg-gray-100 dark:hover:bg-slate-700"}`}
            >
              Tất cả
            </button>
            {groups.map(g => (
              <button
                key={g}
                onClick={() => { setSelectedGroup(g); setPage(1); }}
                className={`w-full text-left px-2 py-1 text-sm rounded flex items-center gap-1 ${selectedGroup === g ? "bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 font-medium" : "text-gray-600 dark:text-slate-400 hover:bg-gray-100 dark:hover:bg-slate-700"}`}
              >
                <span className="text-gray-400">+</span> {g}
              </button>
            ))}
          </div>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 flex flex-col overflow-hidden min-w-0">
        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between px-4 sm:px-6 py-3 sm:py-4 gap-2 border-b border-gray-200 dark:border-slate-700">
          <h1 className="text-xl font-bold text-gray-800 dark:text-slate-100" data-testid="pricebook-title">{currentBookName}</h1>
          <div className="flex items-center gap-2 flex-wrap">
            {hasChanges && (
              <Button onClick={handleSaveAll} className="bg-blue-600 hover:bg-blue-700 text-white text-sm gap-1" disabled={saving} data-testid="btn-save-prices">
                <Save className="w-4 h-4" /> {saving ? "Đang lưu..." : `Lưu (${Object.keys(priceChanges).length})`}
              </Button>
            )}
            <Button onClick={handleExport} className="bg-green-600 hover:bg-green-700 text-white text-sm gap-1" data-testid="btn-export-prices">
              <Download className="w-4 h-4" /> Xuất file
            </Button>
            <div className="flex border border-gray-300 dark:border-slate-600 rounded overflow-hidden">
              <button className="p-1.5 bg-blue-600 text-white"><List className="w-4 h-4" /></button>
              <button className="p-1.5 bg-white dark:bg-slate-700 text-gray-600 dark:text-slate-300"><LayoutGrid className="w-4 h-4" /></button>
            </div>
          </div>
        </div>

        {/* Table */}
        <div className="flex-1 overflow-auto">
          <table className="w-full text-sm" data-testid="pricebook-table">
            <thead className="bg-gray-50 dark:bg-slate-800 sticky top-0 z-10">
              <tr className="border-b border-gray-200 dark:border-slate-700">
                <th className="px-3 py-2.5 text-left font-semibold text-gray-700 dark:text-slate-300 w-[140px]">Mã hàng hóa</th>
                <th className="px-3 py-2.5 text-left font-semibold text-gray-700 dark:text-slate-300">Tên hàng</th>
                <th className="px-3 py-2.5 text-right font-semibold text-gray-700 dark:text-slate-300 w-[120px]">Giá vốn</th>
                <th className="px-3 py-2.5 text-right font-semibold text-gray-700 dark:text-slate-300 w-[150px]">Đơn giá nhập cuối</th>
                <th className="px-3 py-2.5 text-right font-semibold text-gray-700 dark:text-slate-300 w-[140px]">Giá mới</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr><td colSpan={5} className="text-center py-10 text-gray-400">Đang tải...</td></tr>
              ) : products.length === 0 ? (
                <tr><td colSpan={5} className="text-center py-10 text-gray-400">Không có sản phẩm nào</td></tr>
              ) : (
                products.map(p => (
                  <tr key={p.id} className="border-b border-gray-100 dark:border-slate-700 hover:bg-gray-50 dark:hover:bg-slate-800/50" data-testid={`price-row-${p.code}`}>
                    <td className="px-3 py-2 text-gray-700 dark:text-slate-300">{p.code}</td>
                    <td className="px-3 py-2 text-gray-800 dark:text-slate-200">{p.name}</td>
                    <td className="px-3 py-2 text-right text-gray-600 dark:text-slate-400">{p.cost_price ? formatVND(p.cost_price) : "0"}</td>
                    <td className="px-3 py-2 text-right text-gray-600 dark:text-slate-400">{p.last_import_price ? formatVND(p.last_import_price) : "0"}</td>
                    <td className="px-3 py-1.5 text-right">
                      <input
                        type="number"
                        className={`w-full text-right text-sm border rounded px-2 py-1 ${
                          priceChanges[p.id] !== undefined
                            ? "border-blue-400 bg-blue-50 dark:bg-blue-900/30 dark:border-blue-600"
                            : "border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-700"
                        } dark:text-slate-200 focus:outline-none focus:ring-1 focus:ring-blue-500`}
                        value={priceChanges[p.id] !== undefined ? priceChanges[p.id] : p.sell_price}
                        onChange={e => handlePriceChange(p.id, e.target.value)}
                        data-testid={`price-input-${p.code}`}
                      />
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="flex flex-col sm:flex-row items-center justify-between px-4 sm:px-6 py-2 sm:py-3 gap-2 border-t border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-900">
          <div className="flex items-center gap-1">
            <button onClick={()=>setPage(1)} disabled={page<=1} className="p-1.5 rounded hover:bg-gray-100 dark:hover:bg-slate-700 disabled:opacity-30"><ChevronsLeft className="w-4 h-4" /></button>
            <button onClick={()=>setPage(p=>Math.max(1,p-1))} disabled={page<=1} className="p-1.5 rounded hover:bg-gray-100 dark:hover:bg-slate-700 disabled:opacity-30"><ChevronLeft className="w-4 h-4" /></button>
            {Array.from({length:Math.min(5,totalPages)},(_,i)=>{let pn;if(totalPages<=5)pn=i+1;else if(page<=3)pn=i+1;else if(page>=totalPages-2)pn=totalPages-4+i;else pn=page-2+i;return(<button key={pn} onClick={()=>setPage(pn)} className={`w-8 h-8 rounded text-sm ${page===pn?"bg-blue-600 text-white":"hover:bg-gray-100 dark:hover:bg-slate-700 text-gray-600 dark:text-slate-400"}`}>{pn}</button>);})}
            <button onClick={()=>setPage(p=>Math.min(totalPages,p+1))} disabled={page>=totalPages} className="p-1.5 rounded hover:bg-gray-100 dark:hover:bg-slate-700 disabled:opacity-30"><ChevronRight className="w-4 h-4" /></button>
            <button onClick={()=>setPage(totalPages)} disabled={page>=totalPages} className="p-1.5 rounded hover:bg-gray-100 dark:hover:bg-slate-700 disabled:opacity-30"><ChevronsRight className="w-4 h-4" /></button>
          </div>
          <span className="text-sm text-gray-500 dark:text-slate-400" data-testid="pricebook-pagination-info">Hiển thị {totalCount>0?(page-1)*pageSize+1:0} - {Math.min(page*pageSize,totalCount)} trên tổng số {totalCount} sản phẩm</span>
        </div>
      </main>

      <MobileFilterToggle showFilter={showFilter} setShowFilter={setShowFilter} />

      {/* Create PriceBook Dialog */}
      <CreateBookDialog open={createBookDialog} onClose={() => setCreateBookDialog(false)} onSuccess={() => { setCreateBookDialog(false); }} />
    </div>
  );
}

function CreateBookDialog({ open, onClose, onSuccess }) {
  const [name, setName] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (!name.trim()) { toast.error("Vui lòng nhập tên bảng giá"); return; }
    setSubmitting(true);
    try {
      const res = await api.post("/pricebook/books", { name: name.trim() });
      toast.success(res.data.message);
      setName(""); onSuccess();
    } catch (err) { toast.error("Lỗi: " + (err.response?.data?.detail || err.message)); }
    finally { setSubmitting(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader><DialogTitle>Tạo bảng giá mới</DialogTitle></DialogHeader>
        <div><label className="text-sm font-medium text-gray-700 dark:text-slate-300 mb-1 block">Tên bảng giá</label><Input value={name} onChange={e=>setName(e.target.value)} placeholder="VD: Bảng giá VIP, Bảng giá Grab..." data-testid="create-book-name" /></div>
        <DialogFooter className="gap-2"><Button variant="outline" onClick={onClose}>Đóng</Button><Button onClick={handleSubmit} disabled={submitting} className="bg-green-600 hover:bg-green-700 text-white" data-testid="submit-create-book">{submitting?"Đang tạo...":"Tạo bảng giá"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
